/**
 * cardVariants.js — visual "moods" for the card background.
 *
 * Five preset CSS filter recipes that re-paint the existing pack background
 * without needing N additional image files. Free users get a deterministic
 * variant picked from their mood_id hash (so the card looks identical on
 * every reload). Premium users can cycle through all five via the Restyle
 * drawer.
 *
 * The variant only affects the background LAYER — text, typography,
 * accent color, and the watermark stay untouched so legibility never
 * degrades. We tested each filter against all three card styles
 * (relatable / poetic / bold) at both display and export sizes.
 */

export const VARIANTS = [
    {
        id: "default",
        label: "as captured",
        filter: "none",
        accent: "rgb(212, 175, 110)",  // warm gold — matches existing brand
    },
    {
        id: "warm",
        label: "golden hour",
        filter: "saturate(1.15) hue-rotate(-8deg) brightness(1.04)",
        accent: "rgb(232, 168, 95)",
    },
    {
        id: "cool",
        label: "blue hour",
        filter: "saturate(0.92) hue-rotate(18deg) brightness(0.94) contrast(1.06)",
        accent: "rgb(140, 180, 220)",
    },
    {
        id: "faded",
        label: "linen wash",
        filter: "saturate(0.55) brightness(1.08) contrast(0.92)",
        accent: "rgb(218, 195, 168)",
    },
    {
        id: "vivid",
        label: "lit through",
        filter: "saturate(1.45) contrast(1.12) brightness(1.02)",
        accent: "rgb(212, 175, 110)",
    },
    {
        id: "mono",
        label: "midnight",
        filter: "grayscale(0.85) brightness(0.88) contrast(1.15)",
        accent: "rgb(196, 196, 196)",
    },
];

/**
 * Stable per-mood variant pick. Same mood_id always yields the same variant
 * so reloading or sharing a card never changes the visual treatment.
 *
 * NOT cryptographic — just a tiny FNV-ish hash → modulo VARIANTS.length.
 */
export function defaultVariantFor(moodId) {
    if (!moodId || typeof moodId !== "string") return VARIANTS[0];
    let h = 2166136261;
    for (let i = 0; i < moodId.length; i += 1) {
        h ^= moodId.charCodeAt(i);
        h = (h * 16777619) >>> 0;
    }
    const idx = h % VARIANTS.length;
    return VARIANTS[idx];
}

/** Pull a per-card variant override from localStorage (used by paid swap). */
export function getVariantOverride(moodId) {
    if (!moodId) return null;
    try {
        const id = window.localStorage.getItem(`moodcard.variant.${moodId}`);
        if (!id) return null;
        return VARIANTS.find((v) => v.id === id) || null;
    } catch {
        return null;
    }
}

/** Persist a per-card variant override. */
export function setVariantOverride(moodId, variantId) {
    if (!moodId) return;
    try {
        if (variantId) window.localStorage.setItem(`moodcard.variant.${moodId}`, variantId);
        else window.localStorage.removeItem(`moodcard.variant.${moodId}`);
    } catch { /* ignore */ }
}

/**
 * The variant the user actually sees for a card — either their explicit
 * override or the deterministic default. Single entry-point so all
 * consumers (MoodCard, ExportCard, Restyle drawer) agree.
 */
export function resolveVariant(moodId) {
    return getVariantOverride(moodId) || defaultVariantFor(moodId);
}
